import { useCallback, type JSX } from 'react'
import { ArrowLeftFromLineIcon, ArrowRightFromLineIcon } from '@ttab/elephant-ui/icons'
import { useQuery } from '@/hooks'

export const Pagination = ({ total, pageSize = 100 }: {
  total?: number
  pageSize?: number
}): JSX.Element => {
  const [{ page = '1' }, setQuery] = useQuery()

  const setPage = useCallback((page: number) => {
    setQuery({ page: page.toString() })
  }, [setQuery])

  // Undefined when the caller doesn't know the number of hits, in which case
  // the next page always stays reachable.
  const maxNumberOfPages = total !== undefined
    ? Math.max(1, Math.ceil(total / pageSize))
    : undefined

  const buttons = [
    {
      id: 'left-arrow',
      icon: ArrowLeftFromLineIcon,
      onClick: () => {
        const newPage = Number(page) - 1
        if (newPage > 1) {
          setPage(Number(page) - 1)
        } else {
          setQuery({ page: undefined })
        }
      },
      disabled: Number(page) <= 1
    },
    {
      id: 'right-arrow',
      icon: ArrowRightFromLineIcon,
      onClick: () => setPage(Number(page) + 1),
      disabled: maxNumberOfPages !== undefined && Number(page) >= maxNumberOfPages
    }
  ]

  return (
    <div className='flex items-center justify-center gap-4 border-t w-full p-4 dark:bg-secondary'>
      {total === undefined || total > 0
        ? (
            <>
              {buttons.map((button, index) => (
                <div key={button.id} className='flex items-center gap-2'>
                  <div
                    className={`flex items-center gap-2 ${button.disabled ? '' : 'cursor-pointer hover:bg-slate-200 dark:hover:bg-table-focused'}`}
                    onClick={() => {
                      if (button.disabled) {
                        return
                      }
                      button.onClick()
                    }}
                  >
                    <button.icon size={20} className={button.disabled ? 'text-slate-500/40 dark:text-slate-400' : 'text-slate-800 dark:text-slate-100'} />
                  </div>
                  {index === 0 && <span className='text-sm ml-2'>{page}</span>}
                </div>
              ))}
            </>
          )
        : null}
    </div>
  )
}
